import { ChevronLeft, FileText } from "lucide-react";
import { useRef, useState } from "react";
import { Navigate, Outlet, useMatch, useNavigate } from "react-router-dom";
import { ReportSheet, ReportViewer } from "@/components/report-viewer";
import { SessionPicker } from "@/components/session-picker";
import type { ChatSession, ReportPayload } from "@/types";
import { useReportContext } from "./context";

export interface ChatOutletContext {
  reportIframeRef: React.RefObject<HTMLIFrameElement | null>;
}

function latestSession(sessions: ChatSession[]): ChatSession | null {
  if (sessions.length === 0) return null;
  return [...sessions].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  )[0];
}

/**
 * Stays mounted across session switches. Owns the report iframe and the
 * session picker — switching between `/s/A` and `/s/B` only re-renders the
 * <Outlet /> (ChatSessionView), so the iframe never reloads.
 */
export default function PatientReportChatLayout() {
  const { token, test, sessions } = useReportContext();
  const navigate = useNavigate();
  const sessionMatch = useMatch("/r/:token/s/:sessionId");
  const urlSessionId = sessionMatch?.params.sessionId ?? null;
  const isDraft = urlSessionId === "new";
  const activeSessionId = isDraft ? null : urlSessionId;

  const [reportOpen, setReportOpen] = useState(false);
  const reportIframeRef = useRef<HTMLIFrameElement | null>(null);

  const payload = test.reportPayload as ReportPayload;

  function navigateToSummary() {
    navigate(`/r/${token}`, { state: { skipInitialChatRedirect: true } });
  }

  function navigateToSession(sessionId: string) {
    navigate(`/r/${token}/s/${sessionId}`);
  }

  // Bare `/r/:token/s` → redirect to latest session or to a fresh draft.
  if (urlSessionId === null) {
    const latest = latestSession(sessions);
    return (
      <Navigate
        to={`/r/${token}/s/${latest ? latest.id : "new"}`}
        replace
      />
    );
  }

  const outletContext: ChatOutletContext = { reportIframeRef };

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-surface text-slate-950">
      <header className="grid shrink-0 grid-cols-3 items-center border-b border-slate-100 bg-white px-4 py-3">
        <button
          type="button"
          onClick={navigateToSummary}
          className="flex items-center gap-1 text-sm font-medium text-muted hover:text-navy"
        >
          <ChevronLeft className="size-4" />
          Summary
        </button>
        <div className="flex items-center justify-center gap-2">
          <img src="/brain-9-svgrepo-com.svg" alt="" className="size-6" />
          <span className="text-sm font-semibold text-navy">Companion</span>
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setReportOpen(true)}
            className="flex min-h-[44px] items-center gap-1.5 rounded-full border border-green/40 bg-green/10 px-3 text-sm font-medium text-navy lg:hidden"
          >
            <FileText className="size-4" />
            Report
          </button>
        </div>
      </header>

      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
        <div className="hidden min-h-0 border-r border-slate-200 lg:block">
          <ReportViewer
            payload={payload}
            testId={test.id}
            token={token}
            className="h-full"
            iframeRef={reportIframeRef}
          />
        </div>

        <aside className="flex min-h-0 min-w-0 flex-col bg-surface-chat">
          <SessionPicker
            sessions={sessions}
            activeSessionId={activeSessionId}
            isDraftActive={isDraft}
            onSelect={navigateToSession}
            onNewChat={() => navigate(`/r/${token}/s/new`)}
            allowNewChat
          />
          <div className="border-b border-slate-100 bg-white px-4 py-2">
            <p className="text-xs text-muted">
              Ask anything about your report — no judgment.
            </p>
          </div>
          <Outlet context={outletContext} />
        </aside>
      </div>

      <ReportSheet
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        payload={payload}
        testId={test.id}
        token={token}
      />
    </div>
  );
}
